<template>
  <!-- Кнопка генерации документов -->
  <v-menu min-width="200px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-bind="attrs" v-on="on" icon
        :loading="isLoadingDownloadSignedDoc || isGeneratingPdf || isLoadingGetTemplates">
        <v-icon color="amber" size="30" @click="getTemplates()" v-tooltip="{
          content: isGeneratingPdf ? $t('pdf_docs.generating') : $t('bookings.generate_docs'),
          placement: 'bottom-center',
          classes: ['info'],
          targetClasses: ['it-has-a-tooltip'],
          delay: {
            show: 500,
            hide: 500,
          },
        }">
          mdi-file-document-outline
        </v-icon>
      </v-btn>
    </template>
    <v-list v-if="!isLoadingGetTemplates">
      <v-list-item v-for="(template, index) in templates" :key="index" @click="downloadSignedDoc(template)"
        :disabled="isGeneratingPdf || isLoadingDownloadSignedDoc">
        <v-list-item-title>
          {{ template.name }}
          <v-chip v-if="isGeneratingPdf" x-small color="orange" class="ml-2">
            {{ $t('pdf_docs.generating') }}
          </v-chip>
        </v-list-item-title>
      </v-list-item>
    </v-list>
    <v-skeleton-loader v-else type="list-item-three-line" height="150px"></v-skeleton-loader>
  </v-menu>
</template>

<script>
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/en-gb";
import html2pdf from 'html2pdf.js';
import { s3PublicUrl } from '@/utils/s3PublicUrl';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import axios from 'axios';
import { uploadAndSignPdf } from '@/services/pdfSigning';
let uuid = require("uuid");

// Bare axios for direct S3 PUT — второй рубеж поверх предиката origin в utils/apiOrigin.js.
const bareAxios = axios.create();

export default {
  name: 'PdfDocuments',

  props: {
    booking: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      default: () => ({})
    },
    isNative: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      templates: [],
      selectedDocumentId: null,
      isGeneratingPdf: false,
      isLoadingDownloadSignedDoc: false,
      isLoadingDeleteSignedDoc: false,
      isLoadingSendSignedDoc: false,
      isLoadingGetTemplates: false,
    }
  },

  computed: {
    generatedDocuments() {
      return this.booking.generated_documents || [];
    }
  },

  methods: {
    // Получение списка шаблонов
    getTemplates() {
      this.isLoadingGetTemplates = true;
      this.axios
        .get(`/api/v1/small_templates?car_id=${this.booking.car_id}`, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.templates = response.data;
        })
        .catch((error) => {
          this.setError(error, this.$t("errors.templates"));
        })
        .finally(() => (this.isLoadingGetTemplates = false));
    },

    // Основная функция генерации и скачивания PDF
    async downloadSignedDoc(template) {
      this.isLoadingDownloadSignedDoc = true;
      this.isGeneratingPdf = true;
      this.$emit('generating-start', template.name);
      let self = this;

      try {
        // Step 1: get template data from API
        const pdfDataResponse = await this.axios.get(
          `/api/v1/prepare_pdf_data?template_id=${template.id}&booking_id=${this.booking.id}`,
          { headers: { Authorization: this.$store.getters.getAuthToken } }
        );
        const pdfData = pdfDataResponse.data;

        // Step 2: generate PDF locally as Blob
        const pdfBlob = await this.generatePdfFromTemplate(pdfData);

        // Step 3: FT-019 async flow — presign → S3 PUT → finalize → poll
        const document = await uploadAndSignPdf({
          apiClient: this.axios,
          bareAxios: bareAxios,
          bookingId: this.booking.id,
          templateId: template.id,
          pdfBlob,
          authToken: this.$store.getters.getAuthToken,
        });

        this.$emit('document-created', document);

        if (this.isNative) {
          this.downloadFileForMobile(document.download_url);
        } else {
          window.open(document.download_url, '_blank');
        }
      } catch (error) {
        this.setError(error, this.$t("errors.file_upload"));
        console.log(error);
      } finally {
        this.isLoadingDownloadSignedDoc = false;
        this.isGeneratingPdf = false;
        this.selectedDocumentId = null;
        this.$emit('generating-end');
      }
    },
    // Генерация PDF из шаблона на фронтенде
    async generatePdfFromTemplate(pdfData) {
      return new Promise((resolve, reject) => {
        try {
          console.log('Starting PDF generation');

          // Создаем временный контейнер для HTML
          const container = document.createElement('div');
          // FT-019 followup — strengthened hiding to suppress html2pdf flash artifacts.
          // position:fixed removes from document flow (no parent reflow), opacity:0 +
          // pointerEvents:none + zIndex:-9999 keep it imperceptible to user during render.
          // visibility:hidden NOT used — html2canvas needs to measure layout.
          container.style.position = 'fixed';
          container.style.left = '-9999px';
          container.style.top = '0';
          container.style.width = '210mm';
          container.style.opacity = '0';
          container.style.pointerEvents = 'none';
          container.style.zIndex = '-9999';
          container.style.backgroundColor = '#fff';
          container.style.fontFamily = 'Arial, sans-serif';
          container.style.fontSize = '12px';
          container.style.lineHeight = '1.6';
          container.style.boxSizing = 'border-box';
          container.style.display = 'block';

          // Обрабатываем контент шаблона - заменяем переменные
          let processedContent = pdfData.template.content;
          console.log(pdfData.variables);
          Object.keys(pdfData.variables).forEach(key => {
            const regex = new RegExp(`{{${key}}}`, 'g');
            processedContent = processedContent.replace(regex, pdfData.variables[key] || '');
          });

          // Обрабатываем специальные переменные для разрывов страниц
          processedContent = processedContent
            .replace(/{page_brake}/g, '<div class="html2pdf__page-break"></div>')
            .replace(/{page_break}/g, '<div class="html2pdf__page-break"></div>')
            .replace(/{{page_brake}}/g, '<div class="html2pdf__page-break"></div>')
            .replace(/{{page_break}}/g, '<div class="html2pdf__page-break"></div>');

          // Улучшенная обработка для предотвращения разрывов строк
          processedContent = processedContent
            // Добавляем классы для заголовков более аккуратно
            .replace(/<h([1-6])(\s[^>]*)?>/g, function (match, level, attrs) {
              attrs = attrs || '';
              if (attrs.includes('class=')) {
                return `<h${level}${attrs.replace(/class="([^"]*)"/, 'class="$1 avoid-break"')}>`;
              } else {
                return `<h${level}${attrs} class="avoid-break">`;
              }
            })
            // Защищаем таблицы
            .replace(/<table(\s[^>]*)?>/g, function (match, attrs) {
              attrs = attrs || '';
              if (attrs.includes('class=')) {
                return `<table${attrs.replace(/class="([^"]*)"/, 'class="$1 keep-together"')}>`;
              } else {
                return `<table${attrs} class="keep-together">`;
              }
            })
            // Защищаем списки
            .replace(/<ul(\s[^>]*)?>/g, function (match, attrs) {
              attrs = attrs || '';
              if (attrs.includes('class=')) {
                return `<ul${attrs.replace(/class="([^"]*)"/, 'class="$1 keep-together"')}>`;
              } else {
                return `<ul${attrs} class="keep-together">`;
              }
            })
            .replace(/<ol(\s[^>]*)?>/g, function (match, attrs) {
              attrs = attrs || '';
              if (attrs.includes('class=')) {
                return `<ol${attrs.replace(/class="([^"]*)"/, 'class="$1 keep-together"')}>`;
              } else {
                return `<ol${attrs} class="keep-together">`;
              }
            });

          // Безопасная группировка элементов (только если нет конфликтующих классов)
          processedContent = processedContent
            // Группируем заголовки с следующими параграфами
            .replace(/<h([1-6])([^>]*)>(.*?)<\/h[1-6]>\s*<p([^>]*)>/g, function (match, level, hAttrs, content, pAttrs) {
              return `<div class="keep-together"><h${level}${hAttrs}>${content}</h${level}><p${pAttrs}>`;
            })
            // Оборачиваем номера пунктов только в начале параграфов
            .replace(/<p([^>]*)>(\s*)(\d+\.\s*)/g, '<p$1>$2<span class="clause-number">$3</span>');

          // Добавляем подпись если есть
          if (pdfData.signature) {
            processedContent = processedContent.replace(
              /<div class="signature-placeholder start"><\/div>/g,
              `<div style="margin: 20px 0;">
                  <img src="${pdfData.signature.image}" style="max-width: 150px; max-height: 100px; align-self: center;" />
                  <div style="font-size: 10px; color: #666; margin-top: 5px;">
                    ${this.$t('pdf_docs.signer')}: ${pdfData.signature.signer}<br/>
                    ${this.$t('pdf_docs.date_of_signature')}: ${moment(pdfData.signature.created_date).format('DD.MM.YYYY HH:mm')}<br/>
                    ${this.$t('pdf_docs.date_of_generation')}: ${moment(pdfData.signature.generated_date).format('DD.MM.YYYY HH:mm')}
                  </div>
                 </div>`
            );
            processedContent = processedContent.replace(
              /<div class="signature-placeholder end"><\/div>/g,
              `<div style="margin: 20px 0;">
                  <img src="${pdfData.signature.image}" style="max-width: 150px; max-height: 100px; align-self: center;" />
                  <div style="font-size: 10px; color: #666; margin-top: 5px;">
                    ${this.$t('pdf_docs.signer')}: ${pdfData.signature.signer}<br/>
                    ${this.$t('pdf_docs.date_of_signature')}: ${moment(pdfData.signature.created_date).format('DD.MM.YYYY HH:mm')}<br/>
                    ${this.$t('pdf_docs.date_of_generation')}: ${moment(pdfData.signature.generated_date).format('DD.MM.YYYY HH:mm')}
                  </div>
                 </div>`
            );
          }

          // Добавляем стили
          const styledContent = `
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              html, body {
                width: 100%;
                height: 100%;
                margin: 0;
                padding: 0;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 12px;
                line-height: 1.6;
                color: #000;
                background-color: #fff;
                /* Улучшенные настройки переносов */
                word-wrap: break-word;
                overflow-wrap: break-word;
                hyphens: auto;
                -webkit-hyphens: auto;
                -moz-hyphens: auto;
                -ms-hyphens: auto;
              }

              /* Предотвращение разрывов слов */
              p, div, span, td, th, li {
                word-break: keep-all;
                overflow-wrap: break-word;
                hyphens: auto;
                -webkit-hyphens: auto;
                -moz-hyphens: auto;
                -ms-hyphens: auto;
                /* Контроль разрывов строк */
                orphans: 3;
                widows: 3;
              }

              /* Улучшенные настройки для параграфов */
              p {
                margin-bottom: 10px;
                orphans: 3;
                widows: 3;
                page-break-inside: avoid;
                /* Минимальная высота для предотвращения висячих строк */
                min-height: 1.2em;
              }
              /* Специальные правила для таблиц */
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 15px;
                /* Запрещаем разрывы внутри таблиц где это возможно */
                page-break-inside: auto;
                break-inside: auto;
              }

              tr {
                page-break-inside: avoid;
                break-inside: avoid;
                page-break-after: auto;
              }

              td, th {
                padding: 5px;
                border: 1px solid #ccc;
                text-align: left;
                vertical-align: top;
                /* Контроль переносов в ячейках */
                word-wrap: break-word;
                overflow-wrap: break-word;
                hyphens: auto;
                page-break-inside: avoid;
              }

              /* Специальные правила для заголовков */
              h1, h2, h3, h4, h5, h6 {
                margin-bottom: 10px;
                font-weight: bold;
                /* Запрещаем разрывы заголовков */
                page-break-after: avoid;
                page-break-inside: avoid;
                break-after: avoid;
                break-inside: avoid;
                orphans: 4;
                widows: 4;
                /* Привязываем заголовок к следующему элементу */
                keep-with-next: always;
              }

              /* Списки */
              ul, ol {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              li {
                page-break-inside: avoid;
                break-inside: avoid;
                orphans: 2;
                widows: 2;
              }
              .text-center {
                text-align: center;
              }
              .align-center {
                text-align: center;
              }
              .align-justify {
                text-align: justify;
              }
              .align-left {
                text-align: left;
              }
              .align-right {
                text-align: right;
              }
              .text-right {
                text-align: right;
              }
              .float-left {
                float: left;
              }
              .float-right {
                float: right;
              }
              .float-none {
                float: none;
              }
              .float-clear {
                clear: both;
              }
              .float-clear-left {
                clear: left;
              }
              .bold {
                font-weight: bold;
              }
              img {
                max-width: 100%;
                height: auto;
              }
              @page {
                size: A4;
                margin: 10mm 10mm 10mm 10mm;
              }
              #pdf-content {
                width: 190mm;
                background-color: white;
                overflow: visible;
              }
              .html2pdf__page-break {
                page-break-before: always !important;
                break-before: always !important;
                display: block;
                height: 0;
                width: 100%;
                margin: 0;
                padding: 0;
              }
              .page-break {
                page-break-before: always !important;
                break-before: always !important;
                display: block;
                height: 0;
                width: 100%;
                margin: 0;
                padding: 0;
              }

              /* Секции и блоки */
              .section, section, .block, div.block {
                page-break-inside: avoid;
                break-inside: avoid;
              }

              .no-break-section {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: block;
                width: 100%;
                margin-bottom: 5px;
              }

              /* Класс для группировки связанных элементов */
              .keep-together {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }

              /* Контроль переносов для номеров и пунктов */
              .clause-number {
                font-weight: bold;
                page-break-after: avoid;
                break-after: avoid;
                keep-with-next: always;
              }

              /* Избегаем разрывов для важных элементов */
              .avoid-break {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                page-break-after: avoid !important;
                break-after: avoid !important;
              }
            </style>
            <div id="pdf-content">
              ${processedContent}
            </div>
          `;

          container.innerHTML = styledContent;
          document.body.appendChild(container);

          console.log('Container appended to document body');

          setTimeout(() => {
            console.log('Using html2pdf.js library');

            // ПОПРОБУЕМ ПРИНЦИПИАЛЬНО ДРУГИЕ НАСТРОЙКИ
            const options = {
              margin: 10,
              filename: `${pdfData.template.name || 'document'}.pdf`,
              image: { type: 'jpeg', quality: 0.95 },
              html2canvas: {
                scale: 2, // Увеличиваем масштаб для лучшего качества
                // allowTaint снят 2026-08-19: при useCORS он мёртв, а оживая — только вредит.
                // html2canvas (1.4.1, cache-storage): useCORS вычисляется первым, для
                // cross-origin картинки ставит crossOrigin='anonymous', и ранний return по
                // allowTaint===false становится недостижим. Ворота, способные оживить
                // allowTaint, — SUPPORT_CORS_IMAGES, то есть `typeof new Image().crossOrigin
                // !== 'undefined'`: true во всех браузерах с 2013 года.
                // Замер 7 конфигураций (2026-08-19): при useCORS:true значение allowTaint не
                // меняет ни экспорт, ни картинку — до пикселя. А при useCORS:false с
                // allowTaint:true холст тейнтится и toDataURL бросает SecurityError, то есть
                // документ не собирается ВООБЩЕ (без allowTaint он собрался бы без картинки).
                // Не возвращать: пара useCORS+allowTaint выглядит подстраховкой, но страхует
                // только в сторону «договора нет».
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false,
                letterRendering: true,
                windowWidth: container.scrollWidth * 2, // Увеличиваем размер окна
                windowHeight: container.scrollHeight * 2,
                scrollX: 0,
                scrollY: 0,
                imageTimeout: 30000,
                // FT-019 followup — was false (likely debug leftover) causing the
                // internal html2canvas sandbox to linger in DOM and produce a brief
                // visual flash. True is the html2canvas default and discards the
                // sandbox immediately after canvas render.
                removeContainer: true,
                onclone: function (clonedDoc) {
                  // Принудительно задаем размеры
                  const clonedContainer = clonedDoc.querySelector('#pdf-content');
                  if (clonedContainer) {
                    clonedContainer.style.width = '190mm';
                    clonedContainer.style.maxWidth = '190mm';
                    clonedContainer.style.fontSize = '12px';
                    clonedContainer.style.lineHeight = '1.6';

                    // Принудительно разбиваем длинные параграфы
                    const paragraphs = clonedContainer.querySelectorAll('p');
                    paragraphs.forEach(p => {
                      if (p.textContent.length > 300) {
                        const text = p.textContent;
                        const sentences = text.split(/[.!?]+/);
                        let newContent = '';
                        let currentLength = 0;

                        sentences.forEach(sentence => {
                          if (currentLength + sentence.length > 250) {
                            newContent += '</p><p>';
                            currentLength = 0;
                          }
                          newContent += sentence + '. ';
                          currentLength += sentence.length;
                        });

                        p.innerHTML = newContent;
                      }
                    });
                  }
                }
              },
              jsPDF: {
                unit: 'mm',
                format: 'a4',
                orientation: 'portrait',
                compress: true,
                hotfixes: ['px_scaling']
              },
              pagebreak: {
                mode: ['css', 'legacy'], // Включаем CSS режим для разрывов
                before: ['.html2pdf__page-break', '.page-break'],
                after: [],
                avoid: [
                  '.keep-together',
                  '.no-break-section',
                  '.avoid-break',
                  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                  'table',
                  '.clause-number',
                  'tr', 'td', 'th'
                ]
              }
            };

            html2pdf()
              .from(container.querySelector('#pdf-content'))
              .set(options)
              .outputPdf('blob')
              .then(pdfBlob => {
                console.log('PDF generated successfully');
                if (document.body.contains(container)) {
                  document.body.removeChild(container);
                }
                resolve(pdfBlob);
              })
              .catch(error => {
                console.error("HTML2PDF error:", error);
                if (document.body.contains(container)) {
                  document.body.removeChild(container);
                }
                this.setError(error, this.$t("errors.error"));
                reject(error);
              });

          }, 500);
        } catch (error) {
          console.error("Error in PDF generation:", error);
          this.setError(error, this.$t("errors.error"));
          reject(error);
        }
      });
    },

    // Метод для скачивания файла на мобильном устройстве
    async downloadFileForMobile(url) {
      if (Filesystem.checkPermissions()) {
        try {
          const getBase64FromUrl = async (url) => {
            const data = await fetch(url);
            const blob = await data.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                resolve(reader.result);
              };
            });
          };

          const base64Data = await getBase64FromUrl(url);

          const result = await Filesystem.writeFile({
            path: `document_${uuid.v1()}.pdf`,
            data: base64Data,
            directory: Directory.Documents,
          });

          await Share.share({
            title: 'Download document',
            files: [result.uri],
          });
        } catch (error) {
          console.log("downloadFileForMobile error", error);
        }
      } else {
        Filesystem.requestPermissions();
      }
    },

    // Скачивание уже созданного документа.
    // Accepts either a full https URL (document.download_url, preferred) or a
    // legacy raw S3 key (document.link) for back-compat with stale bundles.
    downloadSignedDocBottom(urlOrLink) {
      if (!urlOrLink) {
        return;
      }
      const url = s3PublicUrl(urlOrLink);
      if (this.isNative) {
        if (Filesystem.checkPermissions()) {
          const getBase64FromUrl = async (fileUrl) => {
            const data = await fetch(fileUrl);
            const blob = await data.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.readAsDataURL(blob);
              reader.onloadend = () => {
                const base64data = reader.result;
                resolve(base64data);
              }
            });
          }
          getBase64FromUrl(url).then(
            (base64) => {
              // save file
              async function writeFile() {
                return await Filesystem.writeFile({
                  path: `${uuid.v1()}.pdf`,
                  data: base64,
                  directory: Directory.Documents,
                });
              }
              writeFile().then(
                (result) => {
                  Share.share({
                    title: 'Download document',
                    files: [result.uri],
                  });
                }
              );
            }
          );
        } else {
          Filesystem.requestPermissions();
        }
      } else {
        window.open(url, "_blank");
      }
    },

    // Удаление документа
    deleteSignedDoc(id) {
      this.$confirm({
        title: this.$t("custom.you_sure_title"),
        text: this.$t("custom.you_sure_text"),
        icon: "mdi-alert-circle-outline",
        confirmButtonText: this.$t("custom.you_sure_yes"),
        cancelButtonText: this.$t("custom.you_sure_no"),
      }).then((result) => {
        if (result) {
          let self = this;
          this.isLoadingDeleteSignedDoc = true;
          this.axios
            .delete(`/api/v1/generated_documents/${id}`, {
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((response) => {
              this.$emit('document-deleted', id);
              this.$notify(this.$t("bookings.deleted_title"), this.$t("bookings.deleted_text"), "success");
            })
            .catch((error) => this.setError(error, this.$t("errors.deleted")))
            .finally(() => (this.isLoadingDeleteSignedDoc = false, this.selectedDocumentId = null));
        }
      });
    },

    // Отправка документа по email
    sendSignedDocument(signed_document_id) {
      if (this.client && this.client.email) {
        let self = this;
        this.isLoadingSendSignedDoc = true;
        this.axios
          .post(
            "/api/v1/send_signed_document",
            {
              signed_document_id: signed_document_id,
              booking_id: this.booking.id,
            },
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            }
          )
          .then(() => {
            this.$notify(this.$t("mail.send"), this.$t("mail.send_text"), "success");
          })
          .catch((error) => this.setError(error, this.$t("errors.error")))
          .finally(() => (this.isLoadingSendSignedDoc = false, this.selectedDocumentId = null));
      } else {
        this.$notify(this.$t("errors.no_email"), "", "warning");
      }
    },

    // Форматирование даты создания
    formatCreatedAt(dateString) {
      return moment(dateString).format('DD.MM.YYYY HH:mm');
    },
  },

}
</script>

<style scoped>
/* Стили для компонента PDF документов */
</style>
